import type { EnticerSignal } from "./EnticerSignals/types";
import type { JobCardDataFragment } from "../../graphQL/fragments/__generated__/jobCardData.gqlgenerated";

import { DislikeButton } from "./ActionButtons/DislikeButton";
import { SaveButton } from "./ActionButtons/SaveButton";
import { DefaultJobCard } from "./DefaultJobCard";
import { useJobDislikeActions } from "./useJobDislikeActions";
import { useLogFeatureDisplay } from "../../../homepage/logging/DisplayLog";
import { Pressable, type BoxRef, type BoxStyle } from "../../../mads";
import {
  getSavedJobState,
  setSavedJobState,
  useSavedJobState,
} from "../../../shared/apollo/globalState/jobInteractionsState";
import { useI18n } from "../../../shared/I18n";
import { getCachedViewJobHint } from "../../../shared/native-modules/tare/getCachedViewJobHint";
import { updateViewJobHint } from "../../../shared/native-modules/tare/viewJobHint";
import { setUniversalFocus } from "../../../shared/util/AccessibilityFocusManager";
import { resolveJobSponsored } from "../../../shared/util/urlUtils";
import { useSaveJobMutation } from "../../graphQL/mutations/__generated__/saveJobMutation.gqlgenerated";
import { useRunOnceAfterMount } from "../../util/useRunOnceAfterMount";

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

export type DislikedJobCardProps = {
  job: JobCardDataFragment;
  undoDislikeJob: () => void;
  onPressUpdateJobPreferences?: (job: JobCardDataFragment) => void;
  containerStyle?: BoxStyle;
  updatePreferencesButtonRef?: React.RefObject<BoxRef | null>;
  focusAfterRender?: boolean;
  onDismissDislikedJobCard?: () => void;
};

/**
 * JobCardData extends the GraphQL fragment with trackingKey.
 * Surfaces (homepage, ace, etc.) map their result's trackingKey to this field.
 */
export type JobCardData = JobCardDataFragment & {
  trackingKey?: string | null;
};

export type AppearanceMode = "default" | "featured";

export type JobCardHandle = {
  refreshFromCache: () => Promise<void>;
};

export type JobCardProps = {
  ref?: React.Ref<BoxRef>;
  handleRef?: React.Ref<JobCardHandle>;
  data: JobCardData;
  appearanceMode?: AppearanceMode;
  viewJobFrom?: string;
  dislikeJobPageId: string;
  loggedIn: boolean;
  containerStyle?: BoxStyle;
  onJobCardImpression?: (job: JobCardData) => void;
  onPressJobCard?: (job: JobCardData, viewJobFrom?: string) => void;
  onPressSaveJobButton?: (job: JobCardData) => void;
  onPressDislikeJobButton?: () => void;
  onJobSaved?: (job: JobCardData, undo?: () => void) => void;
  onJobSaveError?: (job: JobCardData, error: unknown) => void;
  onPressUpdateJobPreferences?: (job: JobCardData) => void;
  autoOpenUpdatePreferencesOnDislike?: boolean;
  dislikedJobCardUpdatePreferencesButtonRef?: React.RefObject<BoxRef | null>;
  onWillSaveOrUnsaveJob?: (job: JobCardData, willSave: boolean) => void;
  DislikedJobCard: React.ComponentType<DislikedJobCardProps>;
  clickEffectEnabled?: boolean;
  // show the easily apply badge when applicable
  showEasilyApplyBadge?: boolean;
  trackingKey?: string;
  showApplicationStatusBadge?: boolean;
  /**
   * When true, hides the save + dislike action button toolbar entirely.
   * Defaults to false (toolbar shown when `loggedIn`). Used by surfaces
   * like the PostApplyAd modal that intentionally omit job actions.
   */
  hideActionButtons?: boolean;
  testID?: string;
  onDismissDislikedJobCard?: () => void;
  webTargetUrl?: string | null;
  isSelected?: boolean;
  showRelevanceSurvey?: boolean;
  /** Position/index of this job card within its section */
  position?: number;
  enticerSignals?: EnticerSignal[];
  /** Surface-provided banner image for featured cards (takes priority over featuredCardData). */
  bannerImageUrl?: string;
  /**
   * Size the featured banner to the fixed 1960:400 header ratio (height derived from
   * width) instead of the fixed height. Only applies to the surface `bannerImageUrl`;
   * fallback banner sources stay on the fixed-height path.
   */
  preserveBannerAspectRatio?: boolean;
};

export const JobCard = forwardRef<BoxRef, JobCardProps>((jobCardProps, ref) => {
  const {
    handleRef,
    data: job,
    viewJobFrom,
    dislikeJobPageId,
    containerStyle,
    onJobCardImpression,
    onPressJobCard,
    onJobSaved,
    onJobSaveError,
    onPressUpdateJobPreferences,
    autoOpenUpdatePreferencesOnDislike,
    dislikedJobCardUpdatePreferencesButtonRef,
    onWillSaveOrUnsaveJob,
    DislikedJobCard,
    trackingKey,
    testID,
    appearanceMode = "default",
    onDismissDislikedJobCard,
    isSelected = false,
  } = jobCardProps;

  const i18n = useI18n();
  const viewFullDetailsLabel = i18n.gettext("View full details of {0} at {1}", [
    job.title,
    job.sourceEmployerName,
  ]);

  const pressableRef = useRef<BoxRef>(null);
  const [isDislikeUndo, setIsDislikeUndo] = useState(false);

  const savedJobState = useSavedJobState(job.key, job.savedJob?.state);
  const isSavedJob = savedJobState === "SAVED";

  const { isDislikedJob, dislikeJob, undoDislikeJob } = useJobDislikeActions(
    job.key,
    dislikeJobPageId,
    trackingKey,
  );

  // Refresh state from cache
  const refreshFromCache = useCallback(async () => {
    try {
      const cachedHint = await getCachedViewJobHint(job.key);
      if (cachedHint?.isSaved !== null && cachedHint?.isSaved !== undefined) {
        setSavedJobState(job.key, cachedHint.isSaved ? "SAVED" : "VISITED");
      }
    } catch (error) {
      console.error("Error fetching cached view job hint:", error);
    }
  }, [job.key]);

  // Expose refreshFromCache via handleRef
  useImperativeHandle(
    handleRef,
    () => ({
      refreshFromCache,
    }),
    [refreshFromCache],
  );
  const [saveJobMutation, { loading: isSaveJobMutationLoading }] = useSaveJobMutation();
  const logFeatureDisplay = useLogFeatureDisplay();

  const handleUndoDislikeJob = useCallback(() => {
    undoDislikeJob();

    setIsDislikeUndo(true);
  }, [undoDislikeJob]);

  useEffect(() => {
    if (!isDislikeUndo) return;

    if (pressableRef.current) {
      setUniversalFocus(pressableRef, {
        setAccessibilityFocus: true,
        setKeyboardFocus: true,
        delay: 150,
      });
    }

    setIsDislikeUndo(false);
  }, [isDislikeUndo]);

  const saveOrUnsaveJob = async () => {
    const willSave = !isSavedJob;
    onWillSaveOrUnsaveJob?.(job, willSave);

    const updateJobState = async (state: "VISITED" | "SAVED"): Promise<boolean> => {
      const previousSavedJobState = getSavedJobState(job.key);
      setSavedJobState(job.key, state);

      try {
        const response = await saveJobMutation({
          variables: { jobKey: job.key, state },
        });

        const success = !!response.data?.updateSavedJobState?.savedJob.job.key;
        if (success) {
          const newIsSaved = state === "SAVED";
          await updateViewJobHint(job.key, { isSaved: newIsSaved });
        } else {
          setSavedJobState(job.key, previousSavedJobState);
        }
        return success;
      } catch (err: unknown) {
        setSavedJobState(job.key, previousSavedJobState);
        console.error(`Error while updating job state for jobKey - ${job.key}`, err);
        onJobSaveError?.(job, err);
        return false;
      }
    };

    const success = await updateJobState(willSave ? "SAVED" : "VISITED");

    // Only create undo function when saving job
    if (success && willSave) {
      const undoSaveJob = () => {
        void updateJobState("VISITED");
      };
      onJobSaved?.(job, undoSaveJob);
    }
  };

  useRunOnceAfterMount(() => {
    onJobCardImpression?.(job);
  });

  logFeatureDisplay({
    jobKey: job.key,
    field: "sponsored",
    value: resolveJobSponsored(job.tracking?.jobClick),
  });

  if (isDislikedJob) {
    return (
      <DislikedJobCard
        job={job}
        undoDislikeJob={handleUndoDislikeJob}
        onPressUpdateJobPreferences={onPressUpdateJobPreferences}
        containerStyle={containerStyle}
        updatePreferencesButtonRef={dislikedJobCardUpdatePreferencesButtonRef}
        focusAfterRender
        onDismissDislikedJobCard={onDismissDislikedJobCard}
      />
    );
  }

  const saveButton = (
    <SaveButton
      isSavedJob={isSavedJob}
      isSaveJobMutationLoading={isSaveJobMutationLoading}
      appearanceMode={appearanceMode}
      jobTitle={job.title}
      onPressSave={() => {
        jobCardProps.onPressSaveJobButton?.(job);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME
        saveOrUnsaveJob();
      }}
      testID={
        testID ? (isSavedJob ? `${testID}-unsaveJobButton` : `${testID}-saveJobButton`) : undefined
      }
    />
  );

  const dislikeButton = (
    <DislikeButton
      jobTitle={job.title}
      testID={testID ? `${testID}-dislikeJobButton` : "dislikeJobButton"}
      onPressDislike={() => {
        jobCardProps.onPressDislikeJobButton?.();
        dislikeJob();
        // if saved, undo that
        if (isSavedJob) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME
          saveOrUnsaveJob();
        }
        if (autoOpenUpdatePreferencesOnDislike) {
          onPressUpdateJobPreferences?.(job);
        }
      }}
    />
  );

  const handlePressJobCard = () => {
    onPressJobCard?.(job, viewJobFrom);
  };

  const defaultJobCard = ({ pressed }: { pressed: boolean }) => (
    <DefaultJobCard
      ref={(r) => {
        pressableRef.current = r;
        if (typeof ref === "function") {
          ref(r);
        } else if (ref) {
          ref.current = r;
        }
      }}
      pressed={pressed}
      saveButton={saveButton}
      dislikeButton={dislikeButton}
      jobCardProps={jobCardProps}
      viewFullDetailsLabel={viewFullDetailsLabel}
      onPressJobCard={handlePressJobCard}
      trackingKey={trackingKey}
      showApplicationStatusBadge={jobCardProps.showApplicationStatusBadge}
      isSelected={isSelected}
    />
  );

  const jobCards: Record<AppearanceMode, (props: { pressed: boolean }) => React.ReactNode> = {
    default: defaultJobCard,
    featured: defaultJobCard,
  } as const;

  return (
    <Pressable
      sx={containerStyle}
      // Do not treat this wrapper as one accessibility element; descendants (details action,
      // job-actions toolbar, save/dislike) are exposed to assistive tech instead.
      accessible={false}
      // Do not make this outer Pressable a separate keyboard/TalkBack focus target on Android.
      focusable={false}
      onPress={handlePressJobCard}
      testID={testID ?? `job-card-${job.key}`}
    >
      {({ pressed }) => jobCards[appearanceMode]({ pressed })}
    </Pressable>
  );
});
